/*
 * product-media-adaptive-height.js
 * Makes the product-page image carousel resize its viewport to the ACTIVE
 * slide, instead of staying as tall as the tallest image.
 *
 * Why this is needed: <xo-carousel-inner> is a 1x1 grid whose height is driven
 * by <xo-carousel-list> (a flex row), so the track is always as tall as the
 * tallest slide and every other image gets letterboxed/cropped into it.
 *
 * Approach (cheap, no reflow loops, no vendor-bundle edits):
 *   - Each image already exposes its true ratio via xo-intrinsic-width/height,
 *     so the target height = viewportWidth * (intrinsicHeight / intrinsicWidth).
 *     That means no getBoundingClientRect on images and no wait-for-load.
 *   - Recompute only when the active slide settles (debounced), on load, and on
 *     viewport width changes. A CSS height transition (enabled after the first
 *     paint) smooths the change without animating on initial render.
 */
(function () {
  var MAIN_LIST = '.xo-product-info-media-carousel__list';

  function activeSlide(list) {
    var slides = [];
    var children = list.children;
    for (var i = 0; i < children.length; i++) {
      var c = children[i];
      var tag = c.tagName.toLowerCase();
      if ((tag === 'xo-carousel-slide' || c.hasAttribute('xo-carousel-slide')) && !c.hasAttribute('xo-cloned')) {
        slides.push(c);
      }
    }
    if (!slides.length) return null;

    // Prefer the component's own active/visible marker.
    for (var j = 0; j < slides.length; j++) {
      var a = slides[j].getAttribute('xo-active');
      if (a !== null && a !== 'false') return slides[j];
    }
    for (var k = 0; k < slides.length; k++) {
      var v = slides[k].getAttribute('xo-visible');
      if (v !== null && v !== 'false') return slides[k];
    }

    // Fallback: derive index from the list's translateX (per-view = 1).
    try {
      var m = new DOMMatrixReadOnly(getComputedStyle(list).transform);
      var w = list.clientWidth || 1;
      var idx = Math.round(-m.m41 / w);
      if (idx < 0) idx = 0;
      if (idx > slides.length - 1) idx = slides.length - 1;
      return slides[idx];
    } catch (e) {
      return slides[0];
    }
  }

  function targetHeight(slide, width) {
    var img = slide.querySelector('img[xo-intrinsic-width][xo-intrinsic-height]');
    if (img) {
      var iw = Number(img.getAttribute('xo-intrinsic-width'));
      var ih = Number(img.getAttribute('xo-intrinsic-height'));
      if (iw > 0 && ih > 0) return width * (ih / iw);
    }
    // Non-image media (video / 3D model) or missing dims: measure content once.
    var content = slide.firstElementChild;
    return content ? content.offsetHeight : slide.offsetHeight;
  }

  function apply(inner, list) {
    var slide = activeSlide(list);
    if (!slide) return;
    var width = inner.clientWidth;
    if (!width) return;
    var h = targetHeight(slide, width);
    if (h && h > 0) inner.style.height = Math.round(h) + 'px';
  }

  function init(list) {
    if (list.dataset.adaptiveHeight) return;
    var inner = list.closest('xo-carousel-inner');
    if (!inner) return;
    list.dataset.adaptiveHeight = '1';

    var frame = 0;
    function schedule() {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = 0;
        apply(inner, list);
      });
    }

    // First measurement without a transition, then enable the transition so
    // only subsequent slide changes animate (no shrink-on-load jump).
    apply(inner, list);
    requestAnimationFrame(function () {
      inner.style.transition = 'height 0.35s ease';
    });

    // Re-measure when the active slide changes (transform/attr) or slides swap.
    var mo = new MutationObserver(schedule);
    mo.observe(list, {
      attributes: true,
      attributeFilter: ['style', 'xo-active', 'xo-visible'],
      childList: true,
      subtree: true
    });

    // Late layout / late-decoding images.
    window.addEventListener('load', schedule);

    // Width changes only (height is driven by us, so watching width avoids a loop).
    var lastWidth = inner.clientWidth;
    window.addEventListener(
      'resize',
      function () {
        var w = inner.clientWidth;
        if (w !== lastWidth) {
          lastWidth = w;
          schedule();
        }
      },
      { passive: true }
    );
  }

  function scan(root) {
    var lists = (root || document).querySelectorAll(MAIN_LIST);
    for (var i = 0; i < lists.length; i++) init(lists[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }

  // Theme editor: re-scan when a section is (re)rendered.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
